import { useState } from 'react';
import {
  CircleCheck, ClipboardList, Clock, Download, Package, Search, TriangleAlert, Truck,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { SinDatos, DataState, Badge, Button, Panel, PanelHeader, Table, Thead, Tr, Td } from '@/components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { useOrders } from '@/lib/hooks';
import { exportarOrdenes } from '@/lib/exports';
import { orderStatus, TONE_TEXT } from '@/lib/status';
import { formatCop, formatCompactCop, formatNumber } from '@shared/format';
import type { OrderStatus, PurchaseOrder } from '@shared/types';

const STATUS_ORDER: OrderStatus[] = ['borrador', 'pend_aprobacion', 'en_transito', 'nacionalizacion', 'recibida'];

const STATUS_ICON: Record<OrderStatus, typeof ClipboardList> = {
  borrador: ClipboardList,
  pend_aprobacion: Clock,
  en_transito: Truck,
  nacionalizacion: Package,
  recibida: CircleCheck,
};

const COLUMNS = [
  { key: 'estado', label: 'Estado' },
  { key: 'oc', label: 'N° OC' },
  { key: 'producto', label: 'Producto' },
  { key: 'proveedor', label: 'Proveedor' },
  { key: 'cantidad', label: 'Cantidad', align: 'right' as const },
  { key: 'total', label: 'Total COP', align: 'right' as const },
  { key: 'entrega', label: 'Entrega Est.' },
  { key: 'demora', label: 'Demora', align: 'right' as const },
];

export default function OrdersPage() {
  const orders = useOrders();
  const [search, setSearch] = useState('');

  return (
    <DataState query={orders}>
      {(data) => {
        // Las OC no existen en ninguna base: el B2B registra ventas, no compras a proveedor.
        // Antes esta pantalla mostraba 7 órdenes sembradas.
        if (data.items.length === 0) {
          return (
            <div className="space-y-6">
              <PageHeader title="Órdenes de Compra" subtitle="Seguimiento de lo que viene en camino" />
              <SinDatos
                title="Todavía no aprobaste ninguna compra"
                detail="Acá se siguen las órdenes desde que se deciden hasta que llegan: borrador, en tránsito, nacionalización y recibida."
                comoLlenarlo="Andá a Recomendaciones IA y aprobá una: la orden se crea sola, con la cantidad, el proveedor y la fecha estimada según el tiempo de producción del producto."
              />
            </div>
          );
        }
        const q = search.trim().toLowerCase();
        const filtered = q
          ? data.items.filter((o) =>
              `${o.ocNumber} ${o.sku} ${o.productName} ${o.supplier}`.toLowerCase().includes(q),
            )
          : data.items;
        const summaryByStatus = new Map(data.summary.map((s) => [s.status, s]));

        return (
          <>
            <PageHeader
              title="Órdenes de Compra"
              subtitle={`${data.total} órdenes • ${formatCompactCop(data.inProcess)} COP en proceso`}
              actions={
                <Button
                  variant="subtle"
                  icon={<Download className="h-3.5 w-3.5" />}
                  disabled={data.items.length === 0}
                  onClick={() => exportarOrdenes(data.items)}
                >
                  Exportar
                </Button>
              }
            />

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-5">
              {STATUS_ORDER.map((status) => {
                const meta = orderStatus[status];
                const entry = summaryByStatus.get(status);
                const Icon = STATUS_ICON[status];
                return (
                  <div key={status} className="surface p-4">
                    <div className="flex items-center gap-2">
                      <Icon className={cn('h-4 w-4 shrink-0', TONE_TEXT[meta.tone])} />
                      <span className={cn('text-sm font-semibold', TONE_TEXT[meta.tone])}>{meta.label}</span>
                    </div>
                    <p className="mt-3 text-2xl font-bold text-ink-strong">{entry?.count ?? 0}</p>
                    <p className="mt-0.5 text-xs text-ink-faint">{formatCop(entry?.totalCop ?? 0)}</p>
                  </div>
                );
              })}
            </div>

            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-faint" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por OC, SKU, producto o proveedor…"
                className="h-10 w-full rounded-xl border border-hairline bg-panel pl-10 pr-3 text-sm text-ink-strong placeholder:text-ink-faint focus:border-primary focus:outline-none"
              />
            </div>

            <Panel>
              <Table>
                <Thead columns={COLUMNS} />
                <tbody>
                  {filtered.map((o) => (
                    <OrderRow key={o.ocNumber} order={o} />
                  ))}
                </tbody>
              </Table>
            </Panel>

            {data.notes.filter((n) => n.note).length > 0 && (
              <Panel>
                <PanelHeader title="Notas y observaciones" />
                <div className="divide-y divide-hairline">
                  {data.notes
                    .filter((n) => n.note)
                    .map((n) => (
                      <div key={n.ocNumber} className="flex items-start gap-3 px-5 py-3">
                        <span className="shrink-0 font-mono text-xs text-primary-text">{n.ocNumber}</span>
                        <span className="text-xs text-ink-muted">{n.note}</span>
                      </div>
                    ))}
                </div>
              </Panel>
            )}
          </>
        );
      }}
    </DataState>
  );
}

function OrderRow({ order }: { order: PurchaseOrder }) {
  const meta = orderStatus[order.status];
  const received = order.status === 'recibida';
  return (
    <Tr>
      <Td><Badge tone={meta.tone}>{meta.label}</Badge></Td>
      <Td><span className="font-mono text-primary-text">{order.ocNumber}</span></Td>
      <Td>
        <p className="font-medium text-ink-strong">{order.productName}</p>
        <p className="font-mono text-2xs text-ink-faint">{order.sku}</p>
      </Td>
      <Td><span className="text-ink">{order.supplier}</span></Td>
      <Td align="right"><span className="text-ink-strong">{formatNumber(order.quantity)}</span></Td>
      <Td align="right"><span className="font-semibold text-ink-strong">{formatCompactCop(order.totalCop)}</span></Td>
      <Td>
        <span className={received ? 'text-success-text' : 'text-ink'}>
          {order.deliveryEstimate}
        </span>
      </Td>
      <Td align="right">
        {order.delayDays == null ? (
          <span className="text-ink-faint">—</span>
        ) : (
          <span className="inline-flex items-center justify-end gap-1 text-warning-text">
            <TriangleAlert className="h-3.5 w-3.5" />+{order.delayDays}d
          </span>
        )}
      </Td>
    </Tr>
  );
}
